import { IContextualMenuItem } from "@fluentui/react";
import { useState } from "react";
import { ICaring } from "../../Models/ICaring";
import { ISentinelRich } from "../../Models/ISentinel";
import { ISensor } from "../../Models/ISensor";
import { IUser } from "../../Models/IUser";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { ToastMessage } from "../../Utils/UIMessages";
import DeviceDialog, { DevicePart } from "./DeviceDialog";
import OperatorsDialog from "./OperatorsDialog";
import {
  cameraSummary,
  devLabel,
  dispName,
  hasPerson,
  isWeightZero,
  kindOf,
  mailReason,
  operatorsOf,
  placeOf,
  sensorGivesAlarms,
  sensorTitle,
  sensorToCheck,
} from "./SetupHelpers";
import {
  Btn,
  CARDS_MAX_WIDTH,
  Chips,
  ConfirmDialog,
  Empty,
  ISetupColumn,
  MoreMenu,
  PageHead,
  Pen,
  PenRow,
  Pill,
  SetupCards,
  SetupTable,
  StateText,
  Switch,
  Toolbar,
} from "./SetupUi";

// Scheda Dispositivo (mockup-setup-v2): ogni cella ha la sua matita e la sua finestra; dalla riga si
// cambia solo Attivo (salva subito); nelle Azioni la prova dell'allarme e il menu «...».

type DialogState =
  | { kind: "device"; part: DevicePart; id?: number }
  | { kind: "ops"; id: number }
  | { kind: "off"; id: number }
  | { kind: "delete"; id: number }
  | null;

// motivo breve per cui la prova non si può fare, dai dati della pagina (null = si può)
const testShort = (s: ISentinelRich, ops: IUser[]) => {
  if (s.logicalDeleted) return "Il dispositivo è eliminato.";
  if (!s.enabled) return "Il dispositivo è spento.";
  // senza nome e posizione si prova lo stesso: nell'email compare tipo e numero del dispositivo
  if (!s.patient) return "Il dispositivo non ha una persona collegata.";
  if (ops.length === 0) return "Nessun operatore assegnato.";
  if (!(s.sensors ?? []).some((x: ISensor) => x.enabled && sensorGivesAlarms(x.type))) return "Nessun sensore acceso da provare.";
  return null;
};

const DevicesTab = (props: {
  width: number;
  sentinels: ISentinelRich[];
  users: IUser[];
  carings: ICaring[];
  loaded: boolean;
  reloadSentinels: () => void;
  reloadCarings: () => void;
  onConfigure: (sentinelId: number) => void;
  onTest: (sentinel: ISentinelRich) => void;
}) => {
  const [filter, setFilter] = useState<string>("tutti");
  const [search, setSearch] = useState<string>("");
  const [dialog, setDialog] = useState<DialogState>(null);
  const [savingActive, setSavingActive] = useState<Record<number, boolean>>({});
  const [fresh, setFresh] = useState<number | undefined>();

  const all = props.sentinels;
  const live = all.filter((s) => !s.logicalDeleted);
  const deleted = all.filter((s) => s.logicalDeleted);
  const opsOf = (s: ISentinelRich) => operatorsOf(s, props.carings, props.users);
  const toComplete = (s: ISentinelRich) => !s.logicalDeleted && (!hasPerson(s.patient) || opsOf(s).length === 0);
  const toCheck = (s: ISentinelRich) => (s.sensors ?? []).some(sensorToCheck);
  const q = search.trim().toLowerCase();
  const rows = all
    .filter((s) =>
      filter === "eliminati"
        ? s.logicalDeleted
        : !s.logicalDeleted && (filter === "tutti" || (filter === "ctrl" ? toCheck(s) : toComplete(s)))
    )
    .filter(
      (s) =>
        !q ||
        [s.identifier, s.patient?.identifier, kindOf(s), dispName(s), ...opsOf(s).map((u) => u.identifier)]
          .join(" ")
          .toLowerCase()
          .includes(q)
    )
    .sort((a, b) => b.id - a.id);

  const byId = (id?: number) => all.find((s) => s.id === id);

  // ---------------- Attivo dalla riga: salva subito ----------------

  const setActive = (s: ISentinelRich, on: boolean) => {
    setSavingActive((prev) => ({ ...prev, [s.id]: true }));
    ApiService.SentinelController.UpdateSentinel({ id: s.id, enabled: on } as any, (response: IAPIResponse) => {
      setSavingActive((prev) => ({ ...prev, [s.id]: false }));
      if (response.error !== null) {
        ToastMessage("error", "Impossibile aggiornare il dispositivo.");
        return;
      }
      if (!on) {
        ToastMessage("success", dispName(s) + " spento.");
      } else {
        // il server riaccende i sensori che erano accesi allo spegnimento (sensor.resumeOnEnable)
        const after: any[] = response.parsed?.sensors ?? [];
        const off = after.filter((x) => !x.enabled).length;
        ToastMessage(
          off ? "info" : "success",
          off
            ? dispName(s) + " acceso. " + (off === 1 ? "1 sensore era già spento e resta spento" : off + " sensori erano già spenti e restano spenti") + ": si accendono con la matita dei Sensori."
            : dispName(s) + " acceso, con i suoi sensori."
        );
      }
      props.reloadSentinels();
    });
  };

  const askActive = (s: ISentinelRich) => {
    if (s.enabled) setDialog({ kind: "off", id: s.id });
    else setActive(s, true);
  };

  const doDelete = (s: ISentinelRich) => {
    ApiService.SentinelController.LogicalDeleteSentinel(s.id.toString(), (response: IAPIResponse) => {
      if (response.error === null) {
        ToastMessage("success", dispName(s) + " (n. " + s.id + ") eliminato: è nel filtro Eliminati.");
        props.reloadSentinels();
      } else {
        ToastMessage("error", "Impossibile eliminare il dispositivo.");
      }
    });
  };

  const doRestore = (s: ISentinelRich) => {
    ApiService.SentinelController.LogicalReenableSentinel(s.id.toString(), (response: IAPIResponse) => {
      if (response.error === null) {
        ToastMessage("success", dispName(s) + " ripristinato.");
        props.reloadSentinels();
      } else {
        ToastMessage("error", "Impossibile ripristinare il dispositivo.");
      }
    });
  };

  // ---------------- celle ----------------

  const w = props.width;
  const cards = w < CARDS_MAX_WIDTH;
  const xl = w >= 1440;
  const wide = w >= 1280;
  const mid = w >= 1024;
  const showAttivo = mid;

  const openPart = (s: ISentinelRich, part: DevicePart) => setDialog({ kind: "device", part, id: s.id });
  const openOps = (s: ISentinelRich) => setDialog({ kind: "ops", id: s.id });

  const menuItems = (s: ISentinelRich): IContextualMenuItem[] => {
    if (s.logicalDeleted) {
      return [{ key: "restore", text: "Ripristina", iconProps: { iconName: "Refresh" }, onClick: () => doRestore(s) }];
    }
    const items: IContextualMenuItem[] = [
      {
        key: "configure",
        text: "Configura (tara, strumenti, valori in tempo reale)",
        iconProps: { iconName: "Settings" },
        onClick: () => props.onConfigure(s.id),
      },
    ];
    if (!showAttivo || cards) {
      items.push({
        key: "active",
        text: s.enabled ? "Spegni il dispositivo..." : "Accendi il dispositivo",
        iconProps: { iconName: "Power" },
        onClick: () => askActive(s),
      });
    }
    items.push(
      { key: "div", itemType: 1 },
      {
        key: "delete",
        text: "Elimina dispositivo...",
        iconProps: { iconName: "Delete" },
        onClick: () => setDialog({ kind: "delete", id: s.id }),
      }
    );
    return items;
  };

  const more = (s: ISentinelRich) => (
    <MoreMenu items={menuItems(s)} title={s.logicalDeleted ? "Ripristina" : "Altre azioni di " + dispName(s)} />
  );

  const opsLine = (s: ISentinelRich) => {
    const ops = opsOf(s);
    const rc = ops.filter((u) => !mailReason(u));
    return `${ops.length} ${ops.length === 1 ? "operatore" : "operatori"} · ${rc.length} anche per email`;
  };

  const opsCell = (s: ISentinelRich, compact: boolean) => {
    const ops = opsOf(s);
    const all = ops.map((u) => u.identifier + (mailReason(u) ? " (" + mailReason(u) + ")" : "")).join(", ");
    const inner =
      ops.length > 0 ? (
        <>
          {/* stretta (sotto 1280 px) la riga va a capo invece di finire con i puntini */}
          <span className={compact ? "wrap2" : "el"} title={opsLine(s) + " · " + all}>
            {opsLine(s)}
          </span>
          {!compact && (
            <span className="sub el" title={all}>
              {ops.map((u) => u.identifier).join(", ")}
            </span>
          )}
        </>
      ) : (
        <>
          <Pill kind="warn" text="Nessun operatore" />
          {!compact && (
            <span className="sub el" title="i suoi allarmi non arrivano a nessuno">
              i suoi allarmi non arrivano a nessuno
            </span>
          )}
        </>
      );
    return (
      <PenRow label="Modifica operatori assegnati" onClick={s.logicalDeleted ? undefined : () => openOps(s)}>
        {inner}
      </PenRow>
    );
  };

  const sensNames = (s: ISentinelRich) =>
    (s.sensors ?? [])
      .map((x) => sensorTitle(x.type) + (x.enabled ? "" : " (spento)") + (isWeightZero(x) ? " (soglia 0)" : ""))
      .join(", ") || "nessun sensore";

  const sensSummary = (s: ISentinelRich) => {
    const list = s.sensors ?? [];
    const n = list.length;
    const off = list.filter((x) => !x.enabled).length;
    const base = kindOf(s) === "Varco" && n === 1 && list[0].type === "door" ? "Porta" : n === 1 ? "1 sensore" : n + " sensori";
    const issues: JSX.Element[] = [];
    if (list.some(isWeightZero)) issues.push(<Pill key="z" kind="warn" text="soglia 0" />);
    if (off) issues.push(<Pill key="o" kind="grey" text={off === 1 ? "1 spento" : off + " spenti"} />);
    return (
      <span className="cellrow" title={sensNames(s)}>
        <span style={{ whiteSpace: "nowrap" }}>{base}</span>
        {issues[0]}
        {issues.length > 1 && <span className="sub">+{issues.length - 1}</span>}
      </span>
    );
  };

  const camText = (s: ISentinelRich) => {
    const c = cameraSummary(s.cameraUrl);
    return c ? (
      <span className="el" title={c}>
        {c}
      </span>
    ) : (
      <span className="el muted">Nessuna</span>
    );
  };

  // modulo vocale: grigio come «Nessuna» telecamera quando manca
  const audioText = (s: ISentinelRich) =>
    s.audioEnabled ? <span className="el">Audio attivo</span> : <span className="el muted">Nessun audio</span>;

  const activeSwitch = (s: ISentinelRich) => (
    <Switch
      mini
      on={!!s.enabled}
      saving={!!savingActive[s.id]}
      title={(s.enabled ? "Spegni " : "Accendi ") + dispName(s)}
      onClick={() => askActive(s)}
    />
  );

  const provaArea = (s: ISentinelRich) => {
    const why = testShort(s, opsOf(s));
    return (
      <span className="prova-slot">
        {why ? (
          <span className="na">
            <b>Prova allarme non disponibile</b>
            {why}
          </span>
        ) : (
          <Btn
            text="Prova allarme"
            icon="Ringer"
            title={"Invia un allarme di prova: arriva sulla Home e per email agli operatori di " + dispName(s)}
            onClick={() => props.onTest(s)}
          />
        )}
      </span>
    );
  };

  const stateSuffix = (s: ISentinelRich) => (s.logicalDeleted ? " · eliminato" : !showAttivo && !s.enabled ? " · spento" : "");

  const nameCell = (s: ISentinelRich) => {
    if (!hasPerson(s.patient)) {
      const inner = (
        <>
          <span className="el muted">{s.logicalDeleted ? "Senza nome e posizione" : "Senza nome"}</span>
          <span className="sub el">
            {devLabel(s)}
            {stateSuffix(s)}
          </span>
        </>
      );
      return (
        <PenRow label="Modifica nome e posizione" onClick={s.logicalDeleted ? undefined : () => openPart(s, "name")}>
          {inner}
        </PenRow>
      );
    }
    const where = [devLabel(s), placeOf(s.patient)].filter(Boolean).join(" · ") + stateSuffix(s);
    return (
      <PenRow label="Modifica nome e posizione" onClick={s.logicalDeleted ? undefined : () => openPart(s, "name")}>
        <span className="el" style={{ fontWeight: 600 }} title={s.patient.identifier}>
          {s.patient.identifier}
        </span>
        <span className="sub el" title={where}>
          {where}
        </span>
      </PenRow>
    );
  };

  const columns: ISetupColumn<ISentinelRich>[] = [
    { name: "Nome e posizione", grow: true, width: xl ? 310 : wide ? 260 : mid ? 240 : 214, render: nameCell },
    { name: "Operatori assegnati", grow: true, width: xl ? 260 : wide ? 250 : mid ? 220 : 170, render: (s) => opsCell(s, !wide) },
    {
      name: "Telecamera e audio",
      grow: true,
      width: xl ? 190 : 180,
      hide: !wide,
      render: (s) => (
        <>
          <div className="appline">
            <span className="grow">{camText(s)}</span>
            {!s.logicalDeleted && <Pen label="Modifica telecamera" onClick={() => openPart(s, "cam")} />}
          </div>
          <div className="appline">
            <span className="grow">{audioText(s)}</span>
            {!s.logicalDeleted && <Pen label="Modifica audio" onClick={() => openPart(s, "audio")} />}
          </div>
        </>
      ),
    },
    {
      name: "Sensori",
      grow: true,
      width: xl ? 200 : 190,
      hide: !wide,
      render: (s) => (
        <PenRow label="Modifica sensori" onClick={s.logicalDeleted ? undefined : () => openPart(s, "sensors")}>
          {sensSummary(s)}
        </PenRow>
      ),
    },
    {
      name: "Apparati",
      grow: true,
      width: mid ? 190 : 140,
      hide: wide,
      render: (s) => (
        <>
          <div className="appline">
            <span className="grow">{sensSummary(s)}</span>
            {!s.logicalDeleted && <Pen label="Modifica sensori" onClick={() => openPart(s, "sensors")} />}
          </div>
          <div className="appline">
            <span className="grow sub el">{cameraSummary(s.cameraUrl) ?? "nessuna telecamera"}</span>
            {!s.logicalDeleted && <Pen label="Modifica telecamera" onClick={() => openPart(s, "cam")} />}
          </div>
          <div className="appline">
            <span className="grow sub el">{s.audioEnabled ? "Audio attivo" : "nessun audio"}</span>
            {!s.logicalDeleted && <Pen label="Modifica audio" onClick={() => openPart(s, "audio")} />}
          </div>
        </>
      ),
    },
    {
      name: "Attivo",
      width: xl ? 96 : 88,
      hide: !showAttivo,
      render: (s) =>
        s.logicalDeleted ? (
          <Pill kind="grey" text="Eliminato" />
        ) : (
          <span className="actcell">
            <StateText on={!!s.enabled} />
            {activeSwitch(s)}
          </span>
        ),
    },
    {
      name: "Azioni",
      width: mid ? 248 : 244,
      render: (s) =>
        s.logicalDeleted ? (
          <div className="acts">
            <span className="sub" style={{ width: 84 }}>
              nell'archivio
            </span>
            {more(s)}
          </div>
        ) : (
          <div className="acts">
            {provaArea(s)}
            {more(s)}
          </div>
        ),
    },
  ];

  const card = (s: ISentinelRich) => {
    const head = (
      <>
        <div className="h">
          <span className={hasPerson(s.patient) ? "el" : "el muted"}>{hasPerson(s.patient) ? s.patient.identifier : "Senza nome"}</span>
          <span className="sub">{devLabel(s)}</span>
          {s.logicalDeleted && <Pill kind="grey" text="Eliminato" />}
        </div>
        <div className="r">{more(s)}</div>
      </>
    );
    if (s.logicalDeleted) return <div className="cd deleted">{head}</div>;
    const line = (k: string, v: JSX.Element, right?: JSX.Element) => (
      <>
        <span className="k">{k}</span>
        <div className="cellrow" style={{ minHeight: 40 }}>
          <div className="grow">{v}</div>
          {right}
        </div>
      </>
    );
    return (
      <div className={"cd" + (fresh === s.id ? " fresh" : "")}>
        {head}
        <div className="l">
          <div className="kv">
            {line(
              "Posizione",
              hasPerson(s.patient) ? <span className="el">{placeOf(s.patient) || "-"}</span> : <span className="el muted">mancano nome e posizione</span>,
              <Pen label="Modifica nome e posizione" onClick={() => openPart(s, "name")} />
            )}
            {line(
              "Operatori",
              opsOf(s).length ? <span className="el">{opsLine(s)}</span> : <Pill kind="warn" text="Nessun operatore" />,
              <Pen label="Modifica operatori assegnati" onClick={() => openOps(s)} />
            )}
            {line("Attivo", <StateText on={!!s.enabled} />, activeSwitch(s))}
            {line("Sensori", sensSummary(s), <Pen label="Modifica sensori" onClick={() => openPart(s, "sensors")} />)}
            {line("Telecamera", camText(s), <Pen label="Modifica telecamera" onClick={() => openPart(s, "cam")} />)}
            {line("Audio", audioText(s), <Pen label="Modifica audio" onClick={() => openPart(s, "audio")} />)}
          </div>
        </div>
        <div className="l" style={{ display: "flex", justifyContent: "flex-end" }}>
          {provaArea(s)}
        </div>
      </div>
    );
  };

  const showAll = <Btn text="Mostra tutti" onClick={() => setFilter("tutti")} />;
  const empty = q ? (
    <Empty
      title={"Nessun dispositivo per «" + search.trim() + "»"}
      text="Controlla come è scritto, oppure cerca per nome o operatore."
      action={<Btn text="Cancella la ricerca" onClick={() => setSearch("")} />}
    />
  ) : filter === "eliminati" ? (
    <Empty title="Nessun dispositivo eliminato" text="I dispositivi eliminati restano nell'archivio e compaiono qui, con «Ripristina» nel menu «...»." action={showAll} />
  ) : filter === "ctrl" ? (
    <Empty title="Nessun sensore da controllare" text="Nessun peso con soglia 0 e nessun sensore spento." action={showAll} />
  ) : filter === "dacompletare" ? (
    <Empty title="Nessun dispositivo da completare" text="Ogni dispositivo ha nome e posizione, almeno un operatore e i sensori del suo tipo." action={showAll} />
  ) : (
    <Empty title="Nessun dispositivo" text="Aggiungi il primo letto o varco con «Aggiungi dispositivo»." />
  );

  // ---------------- finestre ----------------

  const closeDialog = () => setDialog(null);
  const dlgSentinel = byId(dialog?.id);

  const renderDialog = () => {
    if (!dialog) return null;
    if (dialog.kind === "device") {
      return (
        <DeviceDialog
          part={dialog.part}
          sentinel={dlgSentinel}
          onClose={closeDialog}
          onSaved={(message: string, id?: number) => {
            setDialog(null);
            ToastMessage(message.startsWith("Non salvati") || message.includes("non sono stati") ? "warning" : "success", message);
            if (dialog.part === "new") {
              setFilter("tutti");
              setSearch("");
              setFresh(id);
            }
            props.reloadSentinels();
          }}
        />
      );
    }
    if (!dlgSentinel) return null;
    if (dialog.kind === "ops") {
      return (
        <OperatorsDialog
          sentinel={dlgSentinel}
          users={props.users}
          carings={props.carings}
          onClose={closeDialog}
          onSaved={(message: string) => {
            setDialog(null);
            ToastMessage(message.includes("non salvati") ? "warning" : "success", message);
            props.reloadCarings();
          }}
        />
      );
    }
    if (dialog.kind === "off") {
      return (
        <ConfirmDialog
          title={"Spegnere " + dispName(dlgSentinel) + "?"}
          text="Non manderà più allarmi né email finché non lo riaccendi. Si spengono anche tutti i suoi sensori e gli allarmi aperti di questo dispositivo vengono chiusi («Sensore disabilitato dall'operatore»). Riaccendendolo, tornano accesi i sensori che lo erano."
          ok="Spegni"
          onClose={closeDialog}
          onOk={() => {
            setDialog(null);
            setActive(dlgSentinel, false);
          }}
        />
      );
    }
    return (
      <ConfirmDialog
        title={"Eliminare " + dispName(dlgSentinel) + " (" + devLabel(dlgSentinel) + ")?"}
        text={"Il dispositivo n. " + dlgSentinel.id + " non compare più nell'app; resta nell'archivio e si può ripristinare dal filtro Eliminati."}
        ok="Elimina"
        onClose={closeDialog}
        onOk={() => {
          setDialog(null);
          doDelete(dlgSentinel);
        }}
      />
    );
  };

  const filterNoun = filter === "eliminati" ? "eliminati" : "dispositivi";
  return (
    <div>
      <PageHead
        title="Dispositivi"
        line="Letti e varchi dell'impianto: nome e posizione, operatori assegnati, sensori, telecamera, audio e prova dell'allarme."
      />
      <Toolbar
        left={
          <>
            <Btn text="Aggiungi dispositivo" icon="Add" primary onClick={() => setDialog({ kind: "device", part: "new" })} />
            <Chips
              chips={[
                { key: "tutti", text: "Tutti (" + live.length + ")" },
                { key: "dacompletare", text: "Da completare (" + live.filter(toComplete).length + ")" },
                { key: "ctrl", text: "Sensori da controllare (" + live.filter(toCheck).length + ")" },
                { key: "eliminati", text: "Eliminati (" + deleted.length + ")" },
              ]}
              selected={filter}
              onChange={setFilter}
            />
          </>
        }
        shown={rows.length}
        total={filter === "eliminati" ? deleted.length : live.length}
        noun={filterNoun}
        search={search}
        onSearch={setSearch}
        placeholder="Cerca dispositivo, persona, operatore"
      />
      {!props.loaded ? (
        <div className="empty">
          <span className="spinner" />
        </div>
      ) : cards ? (
        <SetupCards rows={rows} rowKey={(s) => s.id} card={card} empty={empty} />
      ) : (
        <SetupTable
          columns={columns}
          rows={rows}
          rowKey={(s) => s.id}
          rowClass={(s) => (s.logicalDeleted ? "deleted" : fresh === s.id ? "fresh" : "")}
          empty={empty}
        />
      )}
      {renderDialog()}
    </div>
  );
};

export default DevicesTab;
